import { Duration } from 'date-fns';
import InVivoError from '@/model/InVivoError';
import { Nullable } from '@/model/Common.model';

import { durationParser } from '@/generated/dsl/Duration.parser';
import {
  CompoundDuration,
  Days,
  Dot,
  Hours,
  Minutes,
  Number as NumberNode,
  NumberAndUnit,
  PlainNumber,
  Seconds,
  Sign,
  TimeFormat,
  Unit,
  UnitAndNumber,
} from '@/generated/dsl/Duration.terms';
import { _notNil } from '@/littledash';

export const parser = durationParser.configure({});

type TreeCursor = ReturnType<ReturnType<typeof parser.parse>['cursor']>;

const normalizeZero = (value: number): number => (Math.abs(value) === 0 ? 0 : value);

const parseValue = (numericString: string, multiplier: -1 | 1): number =>
  normalizeZero(parseFloat(numericString) * multiplier);

const mergeDurations = (left: Duration, right: Duration): Duration => {
  const result: Duration = {};
  if (_notNil(left.days) || _notNil(right.days)) {
    result.days = normalizeZero((left.days ?? 0) + (right.days ?? 0));
  }
  if (_notNil(left.hours) || _notNil(right.hours)) {
    result.hours = normalizeZero((left.hours ?? 0) + (right.hours ?? 0));
  }
  if (_notNil(left.minutes) || _notNil(right.minutes)) {
    result.minutes = normalizeZero((left.minutes ?? 0) + (right.minutes ?? 0));
  }
  if (_notNil(left.seconds) || _notNil(right.seconds)) {
    result.seconds = normalizeZero((left.seconds ?? 0) + (right.seconds ?? 0));
  }
  return result;
};

const parseNumberAndUnit = (cursor: TreeCursor, durationString: string, multiplier: -1 | 1): Duration => {
  const duration: Duration = {};

  if (cursor.firstChild()) {
    let value = 0;
    let unitType = 0;

    if (cursor.type.id === NumberNode) {
      value = parseValue(durationString.slice(cursor.from, cursor.to), multiplier);

      // Move to next sibling (could be Dot or Unit)
      if (cursor.nextSibling()) {
        // Skip optional Dot
        // @ts-expect-error: typescript does not understand cursor movement
        if (cursor.type.id === Dot) {
          cursor.nextSibling();
        }

        // Now we should be at Unit
        // @ts-expect-error: typescript does not understand cursor movement
        if (cursor.type.id === Unit) {
          if (cursor.firstChild()) {
            unitType = cursor.type.id;
            cursor.parent();
          }
        }
      }
    }

    // Set duration based on unit type
    switch (unitType) {
      case Days:
        duration.days = value;
        break;
      case Hours:
        duration.hours = value;
        break;
      case Minutes:
        duration.minutes = value;
        break;
      case Seconds:
        duration.seconds = value;
        break;
    }

    cursor.parent();
  }

  return duration;
};

const parseUnitAndNumber = (cursor: TreeCursor, durationString: string, multiplier: -1 | 1): Duration => {
  const duration: Duration = {};

  if (cursor.firstChild()) {
    let unitType = 0;
    let value = 0;

    // First child should be Unit
    if (cursor.type.id === Unit) {
      if (cursor.firstChild()) {
        unitType = cursor.type.id;
        cursor.parent();
      }

      // Move to next sibling (could be Dot or Number)
      if (cursor.nextSibling()) {
        // Skip optional Dot
        // @ts-expect-error: typescript does not understand cursor movement
        if (cursor.type.id === Dot) {
          cursor.nextSibling();
        }

        // Now we should be at Number
        // @ts-expect-error: typescript does not understand cursor movement
        if (cursor.type.id === NumberNode) {
          value = parseValue(durationString.slice(cursor.from, cursor.to), multiplier);
        }
      }
    }

    // Set duration based on unit type
    switch (unitType) {
      case Days:
        duration.days = value;
        break;
      case Hours:
        duration.hours = value;
        break;
      case Minutes:
        duration.minutes = value;
        break;
      case Seconds:
        duration.seconds = value;
        break;
    }

    cursor.parent();
  }

  return duration;
};

const parseCompoundDuration = (cursor: TreeCursor, durationString: string, multiplier: -1 | 1): Duration => {
  let duration: Duration = {};

  if (cursor.firstChild()) {
    do {
      if (cursor.type.id === NumberAndUnit) {
        const parsedDuration = parseNumberAndUnit(cursor, durationString, multiplier);
        duration = mergeDurations(duration, parsedDuration);
      } else if (cursor.type.id === UnitAndNumber) {
        const parsedDuration = parseUnitAndNumber(cursor, durationString, multiplier);
        duration = mergeDurations(duration, parsedDuration);
      }
    } while (cursor.nextSibling());
    cursor.parent();
  }

  return duration;
};

const parseTimeFormat = (cursor: TreeCursor, durationString: string, multiplier: -1 | 1): Duration => {
  const duration: Duration = {};

  if (cursor.firstChild()) {
    const values: Array<number> = [];

    do {
      if (cursor.type.id === NumberNode) {
        values.push(parseInt(durationString.slice(cursor.from, cursor.to), 10));
      }
    } while (cursor.nextSibling());

    // Handle time format: H:M or D:H:M (based on number of values)
    if (values.length === 2) {
      // H:M format
      duration.hours = normalizeZero(values[0] * multiplier);
      duration.minutes = normalizeZero(values[1] * multiplier);
    } else if (values.length === 3) {
      // D:H:M format for 3 values (based on test expectations)
      duration.days = normalizeZero(values[0] * multiplier);
      duration.hours = normalizeZero(values[1] * multiplier);
      duration.minutes = normalizeZero(values[2] * multiplier);
    }

    cursor.parent();
  }

  return duration;
};

const parsePlainNumber = (cursor: TreeCursor, durationString: string, multiplier: -1 | 1): Duration => {
  const duration: Duration = {};

  if (cursor.firstChild()) {
    if (cursor.type.id === NumberNode) {
      duration.hours = parseValue(durationString.slice(cursor.from, cursor.to), multiplier);
    }
    cursor.parent();
  }

  return duration;
};

export const parseDuration = (input: Nullable<string>): Promise<Duration> => {
  const durationString = (input?.trim() ?? '').toUpperCase();

  if (durationString.length === 0) {
    return Promise.reject(new InVivoError(`Blank duration`, { slug: 'blank-duration' }));
  }

  const tree = parser.parse(durationString);
  const cursor: TreeCursor = tree.cursor();
  let duration: Duration = {};

  if (tree.length < durationString.length) {
    return Promise.reject(
      new InVivoError(`Invalid syntax "${durationString}"`, {
        slug: 'invalid-syntax',
      })
    );
  }

  // Check for any error nodes in the parse tree
  const errorCursor: TreeCursor = tree.cursor();
  do {
    if (errorCursor.node.type.isError) {
      return Promise.reject(new InVivoError(`Invalid syntax "${durationString}"`, { slug: 'invalid-syntax' }));
    }
  } while (errorCursor.next());

  if (cursor.firstChild()) {
    let multiplier: -1 | 1 = 1;
    if (cursor.type.id === Sign) {
      multiplier = durationString.slice(cursor.from, cursor.to) === '-' ? -1 : 1;
      cursor.nextSibling();
    }
    // Parse based on expression type
    if (cursor.type.id === CompoundDuration) {
      duration = parseCompoundDuration(cursor, durationString, multiplier);
    } else if (cursor.type.id === NumberAndUnit) {
      duration = parseNumberAndUnit(cursor, durationString, multiplier);
    } else if (cursor.type.id === UnitAndNumber) {
      duration = parseUnitAndNumber(cursor, durationString, multiplier);
    } else if (cursor.type.id === TimeFormat) {
      duration = parseTimeFormat(cursor, durationString, multiplier);
    } else if (cursor.type.id === PlainNumber) {
      duration = parsePlainNumber(cursor, durationString, multiplier);
    }
  }
  return Promise.resolve(duration);
};
